import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Heart, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { formatApiError } from "../lib/api";

const AdminLogin = () => {
  const { t } = useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Bienvenue");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur de connexion");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="admin-login-page">
      <div className="container-narrow pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#002B49] text-sm font-medium transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft size={16} /> {t.admin.backHome}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,43,73,0.2)] border border-black/5 p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#002B49] mx-auto flex items-center justify-center mb-4">
              <Heart size={22} fill="#C5A059" className="text-[#C5A059]" />
            </div>
            <h1 className="font-serif text-3xl text-[#0A192F] font-semibold" data-testid="admin-login-title">
              {t.admin.loginTitle}
            </h1>
            <p className="text-[#64748B] text-sm mt-2">{t.admin.loginSubtitle}</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                {t.admin.email}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 px-4 py-3 rounded-lg border border-[#E5E7EB] focus:border-[#002B49] focus:ring-2 focus:ring-[#002B49]/10 outline-none"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                {t.admin.password}
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-2 px-4 py-3 rounded-lg border border-[#E5E7EB] focus:border-[#002B49] focus:ring-2 focus:ring-[#002B49]/10 outline-none"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center bg-[#002B49] hover:bg-[#001F35] text-white rounded-lg px-7 py-3.5 font-semibold transition-all duration-300 disabled:opacity-60"
              data-testid="admin-login-btn"
            >
              <Lock size={16} className="mr-2" />
              {submitting ? "..." : t.admin.login}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default AdminLogin;
